import { getFormTabErrorCounts } from '../../../../../../lib/formTabErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { Cog, GitBranch, Link2, RefreshCw, ShieldCheck, Target } from 'lucide-react';
import { useForm, type FieldErrors } from 'react-hook-form';
import {
  DashboardPageFormActions,
  DashboardPageFormTabPanel,
  DashboardPageFormTabList,
  DashboardPageFormTab,
} from '@shigo-ui/dashboard-ui';
import { Button, Form } from '@shigo-ui/ui';

import { EditorSectionTabs } from '../../../../../../components/forms/EditorSectionTabs';
import { useEditorSectionControls } from '../../../../../../components/forms/useEditorSection';
import { dashboardT } from '../../../../../../lib/messages';
import { IntegrationEditorTab, type Integration } from '../../../model';
import {
  integrationFormSchema,
  type IntegrationFormValue,
  type IntegrationSaveOutcome,
} from '../../../integrationFormSchema';
import { IntegrationEditorFields } from '../IntegrationEditorFields';

const tabs = [
  {
    value: IntegrationEditorTab.General,
    icon: Cog,
    label: 'integrations.tabs.general',
  },
  {
    value: IntegrationEditorTab.Integration,
    icon: Link2,
    label: 'integrations.tabs.integration',
  },
  {
    value: IntegrationEditorTab.Rewrite,
    icon: RefreshCw,
    label: 'integrations.tabs.rewrite',
  },
  {
    value: IntegrationEditorTab.Targeting,
    icon: Target,
    label: 'integrations.tabs.targeting',
  },
  {
    value: IntegrationEditorTab.Schain,
    icon: GitBranch,
    label: 'integrations.tabs.schain',
  },
  {
    value: IntegrationEditorTab.Pmp,
    icon: ShieldCheck,
    label: 'integrations.tabs.pmp',
  },
] as const;

export function IntegrationEditorForm(props: {
  initialValue: IntegrationFormValue;
  initialIntegration?: Integration | undefined;
  editing: boolean;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (value: IntegrationFormValue) => Promise<IntegrationSaveOutcome | undefined>;
}) {
  'use no memo';
  const [editorSection, setActiveTab] = useEditorSectionControls(
    IntegrationEditorTab.General,
    Object.values(IntegrationEditorTab),
  );
  const form = useForm<IntegrationFormValue>({
    defaultValues: props.initialValue,
    resolver: zodResolver(integrationFormSchema),
    mode: 'onSubmit',
  });

  function invalidSubmit(errors: FieldErrors<IntegrationFormValue>) {
    const counts = getIntegrationTabErrors(errors);
    const firstInvalidSection = tabs.find((tab) => counts[tab.value])?.value;
    if (firstInvalidSection) setActiveTab(firstInvalidSection);
  }

  const tabErrors = getIntegrationTabErrors(form.formState.errors);

  const sections = tabs.map((tab) => {
    const Icon = tab.icon;
    return {
      value: tab.value,
      label: dashboardT(tab.label),
      description: dashboardT(`${tab.label}.description`),
      icon: <Icon aria-hidden="true" />,
      errorCount: tabErrors[tab.value],
    };
  });

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={(event) =>
          void form.handleSubmit(async (value) => {
            const outcome = await props.onSubmit(value);
            if (!outcome) return;
            if (outcome.targetingId !== undefined) {
              form.setValue('targetingId', outcome.targetingId);
            }
            if (outcome.bundlesCount !== undefined) {
              form.setValue('bundlesCount', outcome.bundlesCount);
            }
            if (outcome.ipsCount !== undefined) {
              form.setValue('ipsCount', outcome.ipsCount);
            }
            if (outcome.clearBundleCsv) form.setValue('pendingBundleCsv', null);
            if (outcome.clearIpCsv) form.setValue('pendingIpCsv', null);
          }, invalidSubmit)(event)
        }
      >
        <EditorSectionTabs controls={editorSection}>
          <DashboardPageFormTabList
            navigationLabel={dashboardT('forms.configuration')}
            reviewLabel={dashboardT('forms.reviewErrors')}
            errorLabel={(count) => dashboardT('common.validationErrors', { count })}
          >
            {sections.map((section) => (
              <DashboardPageFormTab
                key={section.value}
                value={section.value}
                description={section.description}
                icon={section.icon}
                errorCount={section.errorCount}
                errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              >
                {section.label}
              </DashboardPageFormTab>
            ))}
          </DashboardPageFormTabList>
          {tabs.map((tab) => (
            <DashboardPageFormTabPanel key={tab.value} value={tab.value}>
              <IntegrationEditorFields
                tab={tab.value}
                editing={props.editing}
                initialIntegration={props.initialIntegration}
              />
            </DashboardPageFormTabPanel>
          ))}
        </EditorSectionTabs>
        <DashboardPageFormActions saveShortcutLabel={dashboardT('forms.saveShortcut')}>
          <Button type="button" variant="outline" disabled={props.saving} onClick={props.onCancel}>
            {dashboardT('integrations.cancel')}
          </Button>
          <Button type="submit" disabled={props.saving}>
            {dashboardT(props.saving ? 'integrations.saving' : 'integrations.save')}
          </Button>
        </DashboardPageFormActions>
      </form>
    </Form>
  );
}

function getIntegrationTabErrors(errors: FieldErrors<IntegrationFormValue>) {
  return getFormTabErrorCounts(
    errors,
    {
      [IntegrationEditorTab.Integration]: [
        'endpointUri',
        'endpointUriApac',
        'endpointUriEu',
        'endpointUriUsEast',
        'endpointUriUsWest',
        'targetCpm',
        'bidFloor',
        'qps',
        'shareOfVoice',
        'biddingTmax',
        'integrationType',
        'gzip',
        'displayManager',
        'advertiserId',
        'publisherId',
        'siteId',
        'appId',
        'placementId',
        'zoneId',
        'tagId',
        'tripleliftInventoryCode',
        'openxDelDomain',
        'unrulyTargetingUuid',
        'openxUnitId',
        'smartAdServerPageId',
        'smartAdServerNetworkId',
        'smartAdServerFormatId',
        'impExt',
        'groupId',
        'bidRequestExtCo',
        'bidRequestExt',
      ],
      [IntegrationEditorTab.Rewrite]: ['integrationRewriteSets', 'listRewrites', 'listSchains'],
      [IntegrationEditorTab.Targeting]: [
        'targeting',
        'customTargeting',
        'liverampTargeting',
        'targetingId',
        'targetingName',
        'bundlesCount',
        'ipsCount',
        'pendingBundleCsv',
        'pendingIpCsv',
      ],
      [IntegrationEditorTab.Schain]: ['schainLogicMode', 'incompleteSchainHandling'],
      [IntegrationEditorTab.Pmp]: ['pmpEnabled', 'privateAuction', 'deals'],
    },
    IntegrationEditorTab.General,
  );
}
